/**
 * Device Health Score — compute & track per-IMEI device health over time.
 * Backend:
 *   POST /api/health-score/compute
 *   GET  /api/health-score/history/{imei}
 */
import React, { useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { HeartPulse, Activity, TrendingUp, Search } from "lucide-react";
import { toast } from "sonner";

function bandColour(band) {
  if (band === "Excellent") return "bg-emerald-500/15 text-emerald-500";
  if (band === "Good") return "bg-primary/15 text-primary";
  if (band === "Fair") return "bg-amber-500/15 text-amber-500";
  return "bg-rose-500/15 text-rose-500";
}

export default function HealthScore() {
  const [form, setForm] = useState({
    imei: "",
    device_model: "",
    battery_pct: 85,
    screen_intact: true,
    repair_count: 0,
    software_up_to_date: true,
    storage_free_pct: 40,
    age_months: 12,
  });
  const [computing, setComputing] = useState(false);
  const [result, setResult] = useState(null);
  const [historyImei, setHistoryImei] = useState("");
  const [history, setHistory] = useState([]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const compute = async () => {
    if (!form.imei || !form.device_model) return toast.error("IMEI and device model required");
    setComputing(true);
    try {
      const r = await api.post("/health-score/compute", {
        ...form,
        battery_pct: Number(form.battery_pct),
        repair_count: Number(form.repair_count),
        storage_free_pct: Number(form.storage_free_pct),
        age_months: Number(form.age_months),
      });
      setResult(r.data);
      toast.success(`Health score: ${r.data.score} · ${r.data.band}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Compute failed");
    }
    setComputing(false);
  };

  const loadHistory = async () => {
    if (!historyImei) return toast.error("Enter an IMEI");
    try {
      const r = await api.get(`/health-score/history/${encodeURIComponent(historyImei)}`);
      setHistory(r.data.history || []);
      if (!r.data.history?.length) toast.info("No history for that IMEI yet");
    } catch (e) {
      toast.error("Failed to load history");
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="health-score-page">
      <div>
        <div className="overline text-secondary flex items-center gap-2"><HeartPulse className="w-3 h-3" /> Device health</div>
        <h1 className="text-3xl font-display font-light mt-1">Device Health Score</h1>
        <p className="mt-2 text-muted-foreground max-w-xl">
          A weighted score (0–100) across battery, screen, repair history, software, storage & freshness.
          Recompute anytime — the history is kept per IMEI.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Activity className="w-4 h-4" /> Compute new score</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>IMEI</Label>
                <Input value={form.imei} onChange={(e) => update("imei", e.target.value)}
                       placeholder="e.g. 356938035643809" data-testid="hs-imei" />
              </div>
              <div>
                <Label>Device model</Label>
                <Input value={form.device_model} onChange={(e) => update("device_model", e.target.value)}
                       placeholder="e.g. iPhone 15 Pro" data-testid="hs-model" />
              </div>
              <div>
                <Label>Battery %</Label>
                <Input type="number" min={0} max={100} value={form.battery_pct}
                       onChange={(e) => update("battery_pct", e.target.value)} data-testid="hs-battery" />
              </div>
              <div>
                <Label>Storage free %</Label>
                <Input type="number" min={0} max={100} value={form.storage_free_pct}
                       onChange={(e) => update("storage_free_pct", e.target.value)} data-testid="hs-storage" />
              </div>
              <div>
                <Label>Repair count</Label>
                <Input type="number" min={0} value={form.repair_count}
                       onChange={(e) => update("repair_count", e.target.value)} data-testid="hs-repairs" />
              </div>
              <div>
                <Label>Age (months)</Label>
                <Input type="number" min={0} value={form.age_months}
                       onChange={(e) => update("age_months", e.target.value)} data-testid="hs-age" />
              </div>
            </div>
            <div className="flex items-center justify-between border border-border rounded-lg px-3 py-2">
              <Label className="mb-0">Screen intact</Label>
              <Switch checked={form.screen_intact} onCheckedChange={(v) => update("screen_intact", v)} data-testid="hs-screen" />
            </div>
            <div className="flex items-center justify-between border border-border rounded-lg px-3 py-2">
              <Label className="mb-0">Software up-to-date</Label>
              <Switch checked={form.software_up_to_date} onCheckedChange={(v) => update("software_up_to_date", v)} data-testid="hs-software" />
            </div>
            <Button className="w-full" onClick={compute} disabled={computing} data-testid="hs-compute-btn">
              {computing ? "Computing…" : "Compute score"}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Result</CardTitle></CardHeader>
          <CardContent>
            {!result ? (
              <div className="py-16 text-center text-muted-foreground text-sm">
                Fill in the form and press "Compute score".
              </div>
            ) : (
              <>
                <div className="text-center">
                  <div className="font-display text-7xl font-light" data-testid="hs-result-score">{result.score}</div>
                  <Badge className={`mt-3 ${bandColour(result.band)}`} data-testid="hs-result-band">{result.band}</Badge>
                </div>
                <div className="mt-6 space-y-2">
                  {Object.entries(result.sub_scores).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between text-sm">
                      <span className="capitalize text-muted-foreground">{k.replace(/_/g, " ")}</span>
                      <span className="font-mono">{Math.round(v)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4" /> History by IMEI</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input value={historyImei} onChange={(e) => setHistoryImei(e.target.value)}
                   placeholder="Enter IMEI" data-testid="hs-history-imei" />
            <Button variant="outline" onClick={loadHistory} data-testid="hs-history-btn">
              <Search className="w-4 h-4 mr-1.5" /> Look up
            </Button>
          </div>
          {history.length > 0 && (
            <div className="space-y-2">
              {history.map(h => (
                <div key={h.id} className="flex items-center justify-between border border-border rounded-lg p-3">
                  <div>
                    <div className="text-sm font-medium">{h.device_model}</div>
                    <div className="text-xs text-muted-foreground">{new Date(h.computed_at).toLocaleString("en-GB")}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={bandColour(h.band)}>{h.band}</Badge>
                    <div className="font-mono text-lg font-semibold">{h.score}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
